

import React, { useState, Suspense, useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/shared/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { AlertCircle, Edit, CheckCircle, XCircle, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useUsers } from '../hooks/useUsers';
import { AdvancedUserTableSkeleton } from './UserTableSkeleton';
import EditUserForm from './EditUserForm';
import { Profile } from '@/features/users/types';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert';
import { format } from 'date-fns';
import { useSupabaseAuth } from '@/shared/hooks/useSupabaseAuth';
import { useCraftsmen } from '@/features/masters/hooks/useCraftsmen';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@/shared/ui/tooltip";

/**
 * Concurrent React機能を活用したユーザーテーブル内容コンポーネント
 * Suspenseと組み合わせることで最適なローディング体験を提供
 */
const UserTableContent: React.FC = () => {
    const { users, isLoading, error, updateUser } = useUsers();
    const { isAdmin, isLoading: isAuthLoading } = useSupabaseAuth();
    const { data: craftsmen } = useCraftsmen();
    const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [sortField, setSortField] = useState<SortField>('created_at');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const handleEditClick = (user: Profile) => {
        setSelectedUser(user);
        setIsEditDialogOpen(true);
    };

    // 職人情報を取得するヘルパー関数
    const getCraftsmanName = (craftsmanId: string | null) => {
        if (!craftsmanId || !craftsmen) return '-';
        const craftsman = craftsmen.find(c => c.id === craftsmanId);
        return craftsman ? craftsman.name : '-';
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortDirection(null);
                setSortField('created_at');
            } else {
                setSortDirection('asc');
            }
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const sortedUsers = useMemo(() => {
        if (!sortDirection || !sortField) {
            return [...users].sort((a, b) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
        }

        return [...users].sort((a, b) => {
            let aValue: string | number = String(a[sortField] ?? '');
            let bValue: string | number = String(b[sortField] ?? '');

            if (sortField === 'created_at') {
                const aDate = new Date(a.created_at).getTime();
                const bDate = new Date(b.created_at).getTime();
                aValue = aDate;
                bValue = bDate;
            } else {
                aValue = typeof a[sortField] === 'string' ? String(a[sortField]).toLowerCase() : String(a[sortField] ?? '');
                bValue = typeof b[sortField] === 'string' ? String(b[sortField]).toLowerCase() : String(b[sortField] ?? '');
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [users, sortField, sortDirection]);

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) return <ArrowUpDown className="h-4 w-4" />;
        if (sortDirection === 'asc') return <ArrowUp className="h-4 w-4" />;
        if (sortDirection === 'desc') return <ArrowDown className="h-4 w-4" />;
        return <ArrowUpDown className="h-4 w-4" />;
    };

    // 高度なSkeletonローディング - SWRキャッシュ対応
    if (isLoading) {
        return (
            <AdvancedUserTableSkeleton
                isAuthLoading={isAuthLoading}
                isUsersLoading={isLoading && !isAuthLoading}
                userCount={users.length}
                hasCachedData={users.length > 0}
            />
        );
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>エラー</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    if (users.length === 0) {
        return (
            <Alert className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>情報なし</AlertTitle>
                <AlertDescription>表示できるユーザーがいません。</AlertDescription>
            </Alert>
        );
    }

    return (
        <TooltipProvider delayDuration={300}>
            <>
                {/* デスクトップ表示 */}
                <div className="hidden md:block overflow-x-auto">
                    <Table className="min-w-full">
                        <TableCaption className="py-4 text-sm text-muted-foreground">
                            ユーザー一覧 ({sortedUsers.length}件)
                            {sortField && sortDirection && (
                                <span className="ml-2 text-xs">
                                    ({sortField === 'username' ? 'ユーザー名' :
                                        sortField === 'name' ? '氏名' :
                                            sortField === 'role' ? '権限' :
                                                sortField === 'is_active' ? 'ステータス' :
                                                    sortField === 'craftsman_id' ? '職人' : '登録日時'}
                                    で{sortDirection === 'asc' ? '昇順' : '降順'}ソート中)
                                </span>
                            )}
                        </TableCaption>
                        <TableHeader>
                            <TableRow className="border-b bg-muted/30 hover:bg-muted/50 transition-colors">
                                <TableHead className="py-3 px-4 font-semibold text-foreground whitespace-nowrap">
                                    <Button
                                        variant="ghost"
                                        className="h-auto p-0 font-semibold hover:bg-transparent"
                                        onClick={() => handleSort('username')}
                                    >
                                        <div className="flex items-center gap-2">
                                            ユーザー名
                                            {getSortIcon('username')}
                                        </div>
                                    </Button>
                                </TableHead>
                                <TableHead className="py-3 px-4 font-semibold text-foreground whitespace-nowrap">
                                    <Button
                                        variant="ghost"
                                        className="h-auto p-0 font-semibold hover:bg-transparent"
                                        onClick={() => handleSort('name')}
                                    >
                                        <div className="flex items-center gap-2">
                                            氏名
                                            {getSortIcon('name')}
                                        </div>
                                    </Button>
                                </TableHead>
                                <TableHead className="py-3 px-4 font-semibold text-foreground whitespace-nowrap">
                                    <Button
                                        variant="ghost"
                                        className="h-auto p-0 font-semibold hover:bg-transparent"
                                        onClick={() => handleSort('role')}
                                    >
                                        <div className="flex items-center gap-2">
                                            権限
                                            {getSortIcon('role')}
                                        </div>
                                    </Button>
                                </TableHead>
                                <TableHead className="py-3 px-4 font-semibold text-foreground whitespace-nowrap">
                                    <Button
                                        variant="ghost"
                                        className="h-auto p-0 font-semibold hover:bg-transparent"
                                        onClick={() => handleSort('is_active')}
                                    >
                                        <div className="flex items-center gap-2">
                                            ステータス
                                            {getSortIcon('is_active')}
                                        </div>
                                    </Button>
                                </TableHead>
                                <TableHead className="py-3 px-4 font-semibold text-foreground whitespace-nowrap">
                                    <Button
                                        variant="ghost"
                                        className="h-auto p-0 font-semibold hover:bg-transparent"
                                        onClick={() => handleSort('craftsman_id')}
                                    >
                                        <div className="flex items-center gap-2">
                                            職人
                                            {getSortIcon('craftsman_id')}
                                        </div>
                                    </Button>
                                </TableHead>
                                <TableHead className="py-3 px-4 font-semibold text-foreground whitespace-nowrap">
                                    <Button
                                        variant="ghost"
                                        className="h-auto p-0 font-semibold hover:bg-transparent"
                                        onClick={() => handleSort('created_at')}
                                    >
                                        <div className="flex items-center gap-2">
                                            登録日時
                                            {getSortIcon('created_at')}
                                        </div>
                                    </Button>
                                </TableHead>
                                <TableHead className="py-3 px-4 text-right font-semibold text-foreground whitespace-nowrap">
                                    <div className="flex items-center gap-2 justify-end">操作</div>
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sortedUsers.map((user) => (
                                <TableRow key={user.id} className="border-b hover:bg-muted/20 transition-colors data-[state=selected]:bg-muted">
                                    <TableCell className="py-3 px-4 font-medium whitespace-nowrap">
                                        {user.username || user.email}
                                    </TableCell>
                                    <TableCell className="py-3 px-4 whitespace-nowrap">
                                        {user.name || '-'}
                                    </TableCell>
                                    <TableCell className="py-3 px-4 whitespace-nowrap">
                                        <Badge variant={user.role === 'ADMIN' ? 'default' : 'secondary'} className="capitalize">
                                            {user.role === 'ADMIN' ? 'Admin' : 'User'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="py-3 px-4 whitespace-nowrap">
                                        <Badge
                                            variant={user.is_active ? 'default' : 'outline'}
                                            className={user.is_active ?
                                                "border-transparent bg-green-100 text-green-700 dark:bg-green-800/60 dark:text-green-400 hover:bg-green-200/80 dark:hover:bg-green-800/80 flex items-center gap-1 capitalize" :
                                                "flex items-center gap-1 capitalize"
                                            }
                                        >
                                            {user.is_active ?
                                                <><CheckCircle className="h-3.5 w-3.5" />有効</> :
                                                <><XCircle className="h-3.5 w-3.5" />無効</>}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="py-3 px-4 whitespace-nowrap">
                                        <span className="text-sm">
                                            {getCraftsmanName(user.craftsman_id)}
                                        </span>
                                    </TableCell>
                                    <TableCell className="py-3 px-4 whitespace-nowrap text-sm text-muted-foreground">
                                        {format(new Date(user.created_at), 'yyyy/MM/dd HH:mm')}
                                    </TableCell>
                                    <TableCell className="text-right py-3 px-4 whitespace-nowrap">
                                        {isAdmin ? (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleEditClick(user)}
                                            >
                                                <Edit className="h-4 w-4" />
                                                <span className="sr-only">編集</span>
                                            </Button>
                                        ) : (
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <span tabIndex={0}>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            disabled
                                                            className="cursor-not-allowed opacity-50"
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                            <span className="sr-only">編集</span>
                                                        </Button>
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>管理者権限が必要です</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>

                {/* モバイル表示 */}
                <div className="block md:hidden space-y-4">
                    <div className="text-sm text-muted-foreground mb-4">
                        ユーザー一覧 ({sortedUsers.length}件)
                        {sortField && sortDirection && (
                            <div className="text-xs mt-1">
                                {sortField === 'username' ? 'ユーザー名' :
                                    sortField === 'name' ? '氏名' :
                                        sortField === 'role' ? '権限' :
                                            sortField === 'is_active' ? 'ステータス' :
                                                sortField === 'craftsman_id' ? '職人' : '登録日時'}
                                で{sortDirection === 'asc' ? '昇順' : '降順'}ソート中
                            </div>
                        )}
                    </div>
                    {sortedUsers.map((user) => (
                        <div key={user.id} className="rounded-lg border bg-card p-4 shadow-sm space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="space-y-1">
                                    <div className="font-medium">{user.username}</div>
                                    <div className="text-sm text-muted-foreground">{user.name || '-'}</div>
                                </div>
                                {isAdmin ? (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleEditClick(user)}
                                    >
                                        <Edit className="h-4 w-4" />
                                        <span className="sr-only">編集</span>
                                    </Button>
                                ) : (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        disabled
                                        className="cursor-not-allowed opacity-50"
                                    >
                                        <Edit className="h-4 w-4" />
                                        <span className="sr-only">編集</span>
                                    </Button>
                                )}
                            </div>

                            <div className="flex items-center gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">権限:</span>
                                    <Badge variant={user.role === 'ADMIN' ? 'default' : 'secondary'}>
                                        {user.role === 'ADMIN' ? 'Admin' : 'User'}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">ステータス:</span>
                                    <Badge
                                        variant={user.is_active ? 'default' : 'outline'}
                                        className={user.is_active ?
                                            "border-transparent bg-green-100 text-green-700 dark:bg-green-800/60 dark:text-green-400 flex items-center gap-1" :
                                            "flex items-center gap-1"
                                        }
                                    >
                                        {user.is_active ?
                                            <><CheckCircle className="h-3 w-3" />有効</> :
                                            <><XCircle className="h-3 w-3" />無効</>}
                                    </Badge>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">職人:</span>
                                    <span className="text-sm">
                                        {getCraftsmanName(user.craftsman_id)}
                                    </span>
                                </div>
                            </div>

                            <div className="text-xs text-muted-foreground">
                                登録日時: {format(new Date(user.created_at), 'yyyy/MM/dd HH:mm')}
                            </div>
                        </div>
                    ))}
                </div>

                {/* 編集ダイアログ */}
                <Dialog
                    open={isEditDialogOpen}
                    onOpenChange={(isOpen) => {
                        setIsEditDialogOpen(isOpen);
                        if (!isOpen) {
                            setSelectedUser(null);
                        }
                    }}
                >
                    <DialogContent className="sm:max-w-[425px]">
                        <DialogHeader>
                            <DialogTitle>ユーザー情報編集</DialogTitle>
                            <DialogDescription>
                                選択したユーザーの情報を編集できます。必要な項目を変更し、保存してください。
                            </DialogDescription>
                        </DialogHeader>
                        {selectedUser && (
                            <EditUserForm
                                user={selectedUser}
                                onClose={() => {
                                    setIsEditDialogOpen(false);
                                    setSelectedUser(null);
                                }}
                                onSave={async (userId, dataToUpdate) => {
                                    await updateUser(userId, dataToUpdate);
                                }}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            </>
        </TooltipProvider >
    );
};

/**
 * 最適化されたユーザーテーブル - Suspense境界を活用
 * ローディング状態とコンテンツ表示の制御によりスムーズなユーザー体験を提供
 */
export const OptimizedUserTable: React.FC = () => {
    return (
        <Suspense fallback={
            <AdvancedUserTableSkeleton
                isAuthLoading={true}
                isUsersLoading={false}
            />
        }>
            <UserTableContent />
        </Suspense>
    );
};

type SortField = 'username' | 'name' | 'role' | 'is_active' | 'craftsman_id' | 'created_at';
type SortDirection = 'asc' | 'desc' | null;